// composables/useAzureAuth.ts
import { ref, computed, onMounted, onUnmounted } from 'vue';
import {
  PublicClientApplication,
  Configuration,
  AuthenticationResult,
  AccountInfo,
  type BrowserAuthError
} from '@azure/msal-browser';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { App } from '@capacitor/app';
import router from '@/router';
import useApi from '@/composeables/useApi';
import {useUserStore} from "@/store/user";
import {LogInResponse} from "@/constants/types/api";

const INTENDED_DESTINATION_KEY = 'intended_destination';

// PKCE helpers (Web Crypto API — available in browser + Capacitor WebView)
function generateRandomString(length: number): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(36).padStart(2, '0')).join('').slice(0, length);
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

// Module-level storage for PKCE verifier and state between auth request and callback
let storedCodeVerifier: string | null = null;
let storedState: string | null = null;

// Save the intended destination before auth redirects (web/desktop only)
export const saveIntendedDestination = () => {
  const currentPath = window.location.pathname + window.location.search;
  // Don't save auth-related paths, root, or dashboard
  if (!currentPath.startsWith('/auth') && currentPath !== '/' && currentPath !== '/dashboard') {
    localStorage.setItem(INTENDED_DESTINATION_KEY, currentPath);
  }
};

// Get the intended destination without clearing (for checking)
export const getIntendedDestination = (): string | null => {
  return localStorage.getItem(INTENDED_DESTINATION_KEY);
};

// Clear the intended destination
export const clearIntendedDestination = () => {
  localStorage.removeItem(INTENDED_DESTINATION_KEY);
};

// Get and clear the intended destination
const getAndClearIntendedDestination = (): string => {
  const destination = localStorage.getItem(INTENDED_DESTINATION_KEY);
  localStorage.removeItem(INTENDED_DESTINATION_KEY);
  return destination || '/dashboard';
};

// MSAL Configuration
const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_APP_ID,
    authority: import.meta.env.VITE_AUTHORITY_URL,
    knownAuthorities: [import.meta.env.VITE_KNOWN_AUTHORITIES],
    redirectUri: (() => {
      if (Capacitor.getPlatform() === 'ios') {
        return 'msauth.com.sakerhetscenter.app://auth';
      } else if (Capacitor.getPlatform() === 'android') {
        return `msauth://com.sakerhetscenter.app/${import.meta.env.VITE_ANDROID_SIGNATURE_HASH}`;
      } else {
        return `${import.meta.env.VITE_APP_URL}/auth`;
      }
    })(),
    navigateToLoginRequestUrl: false,
    postLogoutRedirectUri: (() => {
      if (Capacitor.getPlatform() === 'ios') {
        return 'msauth.com.sakerhetscenter.app://auth';
      } else if (Capacitor.getPlatform() === 'android') {
        return `msauth://com.sakerhetscenter.app/${import.meta.env.VITE_ANDROID_SIGNATURE_HASH}`;
      } else {
        return `${import.meta.env.VITE_APP_URL}/auth/onboarding`;
      }
    })(),
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        console.log(`[MSAL] ${message}`);
      },
      logLevel: 3, // Verbose for debugging
    },
  },
};

// Create a singleton instance
let msalInstance: PublicClientApplication | null = null;

export const useAzureAuth = () => {
  const loading = ref(false);
  const error = ref<string | null>(null);
  const user = ref<AccountInfo | null>(null);
  const idToken = ref<string | null>(null);
  const accessToken = ref<string | null>(null);

  const isAuthenticated = computed(() => !!user.value);

  let appStateChangeListener: any = null;

  // Initialize MSAL
  const initializeMsal = async() => {
    if (!msalInstance) {
      msalInstance = new PublicClientApplication(msalConfig);
      await msalInstance.initialize();

      // Handle redirect response
      await handlePossibleRedirect();

      // Check if user is already logged in
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        user.value = accounts[0];
        // Try to get token silently
        await getTokenSilently();
        await router.replace(getAndClearIntendedDestination());
      }
    }

    // Set up app resume listener for mobile platforms
    if (Capacitor.isNativePlatform() && !appStateChangeListener) {
      appStateChangeListener = await App.addListener('appStateChange', async(state) => {
        if (state.isActive) {
          // App has resumed from background
          await handlePossibleRedirect();
        }
      });

      // Listen for deep links
      await App.addListener('appUrlOpen', async(data) => {

        // Parse the URL to get the auth code
        if (data.url.includes('?code=') || data.url.includes('&code=') || data.url.includes('#code=') || data.url.includes('msauth://')) {
          await handleDeepLinkAuth(data.url);
        }
      });
    }
  };

  // Handle deep link authentication
  const handleDeepLinkAuth = async(url: string) => {
    try {
      // Extract params from query string (response_mode=query)
      const urlObj = new URL(url);
      const params = urlObj.searchParams;
      const code = params.get('code');
      const returnedState = params.get('state');

      // Validate state to prevent CSRF
      if (storedState && returnedState !== storedState) {
        console.error('State mismatch — possible CSRF attack');
        error.value = 'Något gick fel vid inloggning, var god försök igen.';
        return;
      }

      if (code) {
        // Exchange the code for tokens, including the PKCE verifier
        const tokenRequest = {
          code: code,
          scopes: [
            'openid',
            `https://${import.meta.env.VITE_APP_ID}.onmicrosoft.com/api/user_read`,
          ],
          redirectUri: msalConfig.auth.redirectUri!,
          codeVerifier: storedCodeVerifier || undefined,
        };

        const response = await msalInstance!.acquireTokenByCode(tokenRequest);

        // Clear stored PKCE/state values
        storedCodeVerifier = null;
        storedState = null;

        handleAuthResponse(response);

        await authenticateWithBackend(response.idToken);

        // Close the browser
        await Browser.close();
      }
    } catch (err) {
      console.error('Deep link auth error:', err);
      error.value = 'Något gick fel vid inloggning via länk, var god försök igen eller kontakta support.';
    }
  };

  // Handle possible redirect (separated into its own function)
  const handlePossibleRedirect = async() => {
    if (!msalInstance) return;
    try {
      const response = await msalInstance.handleRedirectPromise();
      if (response) {
        // User is coming back from Azure AD with auth code/tokens
        handleAuthResponse(response);

        // After successful auth, redirect to intended page
        if (response.account) {
          // Call your backend to validate/register the user
          await authenticateWithBackend(response.idToken);
        }
      }
    } catch (err) {
      console.error('Error handling redirect:', err);
    }
  };

  // Handle authentication response
  const handleAuthResponse = (response: AuthenticationResult) => {
    user.value = response.account;
    idToken.value = response.idToken;
    accessToken.value = response.accessToken;

    // Store tokens in localStorage if needed
    localStorage.setItem('msal_idToken', response.idToken);
    localStorage.setItem('msal_accessToken', response.accessToken);
  };

  // Sign in function
  const signIn = async() => {
    loading.value = true;
    error.value = null;

    try {
      await initializeMsal();

      const loginRequest = {
        scopes: [
          'openid',
          `https://${import.meta.env.VITE_APP_ID}.onmicrosoft.com/api/user_read`,
        ],
        prompt: 'login' as const,
      };

      let response: AuthenticationResult;

      if (Capacitor.isNativePlatform()) {
        // For mobile platforms, construct the auth URL manually with PKCE
        const codeVerifier = generateRandomString(43);
        const codeChallenge = await generateCodeChallenge(codeVerifier);
        const state = generateRandomString(32);
        const nonce = generateRandomString(32);

        // Store for use in deep link callback
        storedCodeVerifier = codeVerifier;
        storedState = state;

        const authUrlParams = new URLSearchParams({
          client_id: msalConfig.auth.clientId,
          response_type: 'code',
          redirect_uri: msalConfig.auth.redirectUri!,
          scope: loginRequest.scopes.join(' '),
          prompt: loginRequest.prompt,
          response_mode: 'query',
          code_challenge: codeChallenge,
          code_challenge_method: 'S256',
          state: state,
          nonce: nonce,
        });

        const authUrl = `${msalConfig.auth.authority}/oauth2/v2.0/authorize?${authUrlParams.toString()}`;

        // Open in Capacitor Browser
        await Browser.open({ url: authUrl });

        await Browser.addListener('browserFinished', async() => {
          setTimeout(() => {
            if (!user.value) {
              error.value = 'Inloggningen avbröts av användaren.';
              loading.value = false;
            }

            Browser.removeAllListeners();
          }, 3000);
        });

        // The response will be handled via deep link
        return;
      } else {
        // For web, use popup
        response = await msalInstance!.loginPopup(loginRequest);
        handleAuthResponse(response);

        // Call your backend API with the token
        await authenticateWithBackend(response.idToken);
      }

    } catch (err: { errorCode?: string, message?: string } | any) {
      console.error('Authentication error:', err);
      if (err?.errorCode === 'user_cancelled') {
        error.value = 'Inloggningen avbröts av användaren.';
        loading.value = false;
        return;
      }
      error.value = 'Något gick fel, var god försök igen eller kontakta support.';
    }
  };

  // Get token silently
  const getTokenSilently = async(): Promise<string | null> => {
    if (!msalInstance) {
      await initializeMsal();
    }

    try {
      const accounts = msalInstance!.getAllAccounts();
      if (accounts.length === 0) {
        return null;
      }

      const silentRequest = {
        scopes: [
          'openid',
          `https://${import.meta.env.VITE_APP_ID}.onmicrosoft.com/api/user_read`,
        ],
        account: accounts[0],
      };

      const response = await msalInstance!.acquireTokenSilent(silentRequest);
      idToken.value = response.idToken;
      accessToken.value = response.accessToken;
      return response.idToken;

    } catch (error) {
      console.error('Silent token acquisition failed:', error);
      // Token expired or not found, need to login again
      return null;
    }
  };

  // Authenticate with your backend
  const authenticateWithBackend = async(token: string) => {
    const { apiPost, lastError } = useApi();
    const userStore = useUserStore();
    const res = await apiPost<LogInResponse>('/users/login2', {
      id_token: token,
    });

    if (res) {
      await userStore.signIn(res);
      await router.replace(getAndClearIntendedDestination());
    } else {
      error.value = lastError.value || 'Kunde inte logga in, var god försök igen eller kontakta support.';
    }

    loading.value = false;
  };

  // Sign out
  const signOut = async() => {
    if (!msalInstance) return;

    loading.value = true;

    try {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length === 0) return;

      // Clear local storage
      localStorage.removeItem('msal_idToken');
      localStorage.removeItem('msal_accessToken');

      // Clear MSAL cache
      await msalInstance.clearCache();

      // Clear state
      user.value = null;
      idToken.value = null;
      accessToken.value = null;
    } catch (err: any) {
      console.error('Logout error:', err);
      error.value = err.message || 'Logout failed';
    } finally {
      loading.value = false;
    }
  };

  // Get current user info
  const getCurrentUser = () => {
    if (!msalInstance) return null;
    const accounts = msalInstance.getAllAccounts();
    return accounts.length > 0 ? accounts[0] : null;
  };

  // Clean up listener when component is unmounted
  const cleanup = () => {
    if (appStateChangeListener) {
      appStateChangeListener.remove();
      appStateChangeListener = null;
    }
  };

  return {
    // State
    loading,
    error,
    user,
    idToken,
    accessToken,
    isAuthenticated,

    // Methods
    initializeMsal,
    signIn,
    signOut,
    getTokenSilently,
    getCurrentUser,
    cleanup,
  };
};

// Plugin initialization for app.ts
export const initializeAuth = async() => {
  // Save intended destination before any auth redirects
  saveIntendedDestination();

  if (!msalInstance) {
    msalInstance = new PublicClientApplication(msalConfig);
    await msalInstance.initialize();

    // Handle redirect if coming back from auth
    await msalInstance.handleRedirectPromise();

    // Check if user is already logged in
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      const destination = getAndClearIntendedDestination();
      await router.replace(destination);
    }
  }
  return msalInstance;
};
